import type { OwnerListsApi } from "./ownerListsApi";
import { z } from "zod";
import { TurnChangesSchema } from "../lib/turnChanges";
import { ProductDetailsSchema, ProductRevisionSchema, ShopSettingsSchema, ShopVariantSchema, ShopCategoryInputSchema, CustomizationSetInputSchema, ShopFileInputSchema,
  type ProductChange, type ProductRevision as ShopProductRevision, type ShopSettings, type ShopVariant, type ShopOption, type ShopCategoryInput, type CustomizationSetInput, type ShopStockInput, type ShopFileInput } from "@stackje/site-builder";

/**
 * Client view of the private builder API in `packages/site-builder/src/http.ts`.
 * Parsed at the boundary so the workspace never renders unexpected shapes.
 */

export const ActionStateSchema = z.enum([
  "queued",
  "running",
  "awaiting-confirmation",
  "succeeded",
  "conflict",
  "failed",
  "cancelled",
]);
export type ActionState = z.infer<typeof ActionStateSchema>;

export const PublicationReviewSchema = z.object({
  kind: z.enum(["publish", "rollback", "unpublish"]),
  draftId: z.string().nullable(),
  expectedServingEpoch: z.number().int().nonnegative(),
});
export type PublicationReview = z.infer<typeof PublicationReviewSchema>;

export const TYPEFACE_IDS = ["editorial", "classic", "literary", "modern", "geometric", "friendly", "statement"] as const;
export const TypefaceSchema = z.enum(TYPEFACE_IDS);
export type Typeface = z.infer<typeof TypefaceSchema>;

export const BrandVoiceSchema = z.object({
  tone: z.string(),
  audience: z.string().optional(),
  write: z.string().optional(),
  avoid: z.string().optional(),
});
export type BrandVoice = z.infer<typeof BrandVoiceSchema>;

/** Two Google Fonts families: headings and body text. */
export const BrandFontsSchema = z.object({ display: z.string(), body: z.string(), href: z.string() });
export type BrandFonts = z.infer<typeof BrandFontsSchema>;
export type BrandFontChoice = Pick<BrandFonts, "display" | "body">;

/** Brand guidelines: every design and every new piece of copy follows them. */
export const BrandFieldsSchema = z.object({
  accent: z.string(),
  typeface: TypefaceSchema.optional(),
  fonts: BrandFontsSchema.optional(),
  palette: z.array(z.string()).optional(),
  voice: BrandVoiceSchema.optional(),
});
export type BrandFields = z.infer<typeof BrandFieldsSchema>;

/** What a page change did, as the server worked it out from the two drafts. */
export const ActionSummarySchema = z.object({
  id: z.string(),
  kind: z.string(),
  state: ActionStateSchema,
  proposalDigest: z.string().nullable(),
  result: z.record(z.unknown()).nullable(),
  failure: z.string().nullable(),
  review: PublicationReviewSchema.nullable(),
  /** The draft a draft edit started from; lets the owner undo an applied design change. */
  baseDraftId: z.string().nullable().optional(),
  /** For page changes: what was added, removed and kept. */
  /** For a change to one piece of content: which one, as the owner knows it. */
  subject: z.object({ recordId: z.string(), label: z.string() }).nullable().optional(),
  /** When it was first submitted (ISO time). */
  createdAt: z.string().optional(),
  /** Whether the admin (from a conversation) or the owner made it. */
  by: z.enum(["admin", "owner"]).optional(),
  /** A small design change that also changed text: Undo brings back the whole earlier version. */
  edited: z.boolean().optional(),
  changes: z.object({ records: z.array(z.string()), collections: z.array(z.string()), brand: z.boolean(), design: z.boolean(), hiddenNodes: z.array(z.object({ file: z.string(), nodeId: z.string() })), redirects: z.literal(true).optional(), modules: z.literal(true).optional(), truncated: z.boolean().optional() }).optional(),
  undoes: z.string().optional(),
});
export type ActionSummary = z.infer<typeof ActionSummarySchema>;

export const SiteSummarySchema = z.object({
  id: z.string(),
  draftId: z.string(),
  draftSequence: z.number().int(),
  activeReleaseId: z.string().nullable(),
  publishIntentSequence: z.number().int(),
  servingEpoch: z.number().int().nonnegative(),
  /** What the platform turned on for this site (`blog`, `shop`). Owners can't change it. */
  features: z.array(z.string()),
});
export type SiteSummary = z.infer<typeof SiteSummarySchema>;

const DraftManifestBase = {
  schemaVersion: z.literal(1),
  siteId: z.string(),
  draftId: z.string(),
  parentDraftId: z.string().nullable(),
  sequence: z.number().int(),
  contentVersionId: z.string(),
  canonicalOrigin: z.string(),
  digest: z.string(),
};

export const DraftManifestSchema = z.object({
  ...DraftManifestBase,
  rendererVersion: z.literal("bundle-v1"),
  designDigest: z.string(),
  contentDigest: z.string(),
  brandDigest: z.string(),
  presentationDigest: z.string(),
  routes: z.record(z.string()),
  workspace: z.object({ provider: z.literal("sprites"), id: z.string(), checkpointId: z.string().optional() }),
});
export type DraftManifest = z.infer<typeof DraftManifestSchema>;

export const WorkspacePageSchema = z.object({
  id: z.string(),
  path: z.string(),
  title: z.string(),
  previewUrl: z.string(),
  /** A required error page, opened at a reserved preview address, outside the public routes. */
  special: z.literal("not-found").optional(),
  /** A module's template (the blog's Post page or Tag page), previewing one item under its list page; not a page to rename. */
  template: z.object({ kind: z.enum(["post", "tag", "product", "category"]), parent: z.string() }).optional(),
});
export type WorkspacePage = z.infer<typeof WorkspacePageSchema>;

/** How the site shows up in browser tabs, search results and link previews. Empty fields fall back to `defaults`. */
export const SiteSeoSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  icon: z.object({ src: z.string() }).nullable(),
  image: z.object({ src: z.string() }).nullable().optional(),
  /** The Google tag ID published pages carry; a paid feature like the icon. */
  analytics: z.string().nullable().optional(),
  /** The site's language (BCP 47) and IANA time zone, when the owner has set them. */
  language: z.string().nullable().optional(),
  timeZone: z.string().nullable().optional(),
  defaults: z.object({ title: z.string(), description: z.string().nullable(), icon: z.string() }),
  /** Each page's own details: the owner's overrides (null when automatic), its short name and the description its copy gives it. */
  pages: z.array(z.object({ path: z.string(), label: z.string().nullable(), title: z.string().nullable(), description: z.string().nullable(), pageDescription: z.string().nullable() })),
  /** What a page without its own or a site description falls back to. */
  fallbackDescription: z.string().nullable(),
});
export type SiteSeo = z.infer<typeof SiteSeoSchema>;

export const WorkspaceSchema = z.object({
  site: SiteSummarySchema,
  manifest: DraftManifestSchema,
  /** The business name the owner gave; null until the first draft exists. */
  siteName: z.string().nullable().optional(),
  seo: SiteSeoSchema.optional(),
  /** The owner's brand guidelines, or null before they have any. */
  brand: BrandFieldsSchema.nullable().optional(),
  /** The current design's colours and type, a starting point for brand guidelines. */
  look: z.object({ accent: z.string(), background: z.string(), typeface: TypefaceSchema.optional(), fonts: BrandFontsSchema.optional() }).optional(),
  pages: z.array(WorkspacePageSchema),
  /** Spots the designer left for the owner to fill in; they never show on the published site until then. */
  slotCount: z.number().int().nonnegative().optional(),
  actions: z.array(ActionSummarySchema),
  liveDraftId: z.string().nullable().optional(),
});
export type Workspace = z.infer<typeof WorkspaceSchema>;

export const ActionEnvelopeSchema = z.object({ action: ActionSummarySchema });

export interface PublishRequest {
  actionId: string;
  kind: "publish";
  baseDraftId: string;
  expectedServingEpoch: number;
}

/** Puts an earlier published version live again, or takes the site offline. Neither needs a draft. */
export type ServingRequest =
  | { actionId: string; kind: "rollback"; releaseId: string; expectedServingEpoch: number }
  | { actionId: string; kind: "unpublish"; expectedServingEpoch: number };

/** Anything `POST /publications` prepares. */
export type PublicationRequest = PublishRequest | ServingRequest;

/** An inspiration image the owner shared with a message, as an owner-only thumbnail. */
export const MessageAttachmentSchema = z.object({ id: z.string(), src: z.string(), alt: z.string() });
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;

/** A comment an owner's message sent, shown as a numbered chip that leads back to its pin. */
export const MessageCommentSchema = z.object({ id: z.string(), number: z.number().int().positive(), pagePath: z.string(), note: z.string() });
export type MessageComment = z.infer<typeof MessageCommentSchema>;

/** A card in the transcript: under a Stax reply, the design change it made with the way to its history; or an answered question. */
export const MessageCardSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("design-updated"), actionId: z.string() }),
  /** The owner's answer to a question Stax asked before a change, in the past tense. Shown in place of their message. */
  z.object({ kind: z.literal("confirmation-answered"), decision: z.enum(["confirmed", "cancelled"]), text: z.string(), buyerData: z.boolean().optional() }),
  /** Stax's suggestion for words the owner selected in a post, and what the owner did with it. `original` is the selection they sent. */
  z.object({ kind: z.literal("product-suggestion"), turnId: z.string(), productId: z.string(), name: z.string(), revision: z.string(), expectedRevision: z.string(), rowVersion: z.number().int(), decision: z.enum(["applied", "kept", "changed"]).optional() }),
  z.object({ kind: z.literal("post-rewrite"), index: z.number().int().min(0).max(2).optional(), mode: z.literal("passage").optional(), turnId: z.string(), postId: z.string(), revision: z.string(), from: z.number().int(), to: z.number().int(), original: z.string(), replacement: z.string(), decision: z.enum(["replaced", "kept", "retry"]).optional() }),
]);
export type MessageCard = z.infer<typeof MessageCardSchema>;

/** A web page Stax read to answer, shown under its reply, with any screenshots it took of it. */
export const MessageSourceSchema = z.object({
  url: z.string(),
  title: z.string().optional(),
  failed: z.boolean().optional(),
  screenshots: z.array(MessageAttachmentSchema).optional(),
});
export type MessageSource = z.infer<typeof MessageSourceSchema>;

/** A drawing Stax made for a reply, shown as a card under it. `src` is an owner-only image link; `placed` is whether the site uses it. */
export const MessageIllustrationSchema = z.object({
  id: z.string(),
  src: z.string(),
  alt: z.string(),
  summary: z.string().optional(),
  placed: z.boolean(),
});
export type MessageIllustration = z.infer<typeof MessageIllustrationSchema>;

export const ConversationMessageSchema = z.object({
  buyerData: z.boolean().optional(),
  id: z.string(),
  role: z.enum(["owner", "admin"]),
  text: z.string(),
  attachments: z.array(MessageAttachmentSchema).optional(),
  comments: z.array(MessageCommentSchema).optional(),
  card: MessageCardSchema.optional(),
  sources: z.array(MessageSourceSchema).optional(),
  illustrations: z.array(MessageIllustrationSchema).optional(),
  changes: TurnChangesSchema.optional(),
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

/**
 * What the designer is doing: an action, or planning the next one (after `last`). The client words it.
 * Kinds are read loosely, so a tab loaded before a deploy that adds a kind shows the default line
 * instead of failing to read its progress; `activityLabel` knows the kinds and their fields.
 */
const DesignActionSchema = z.object({ kind: z.string(), page: z.string().optional(), pages: z.number().optional() });
export const DesignActivitySchema = DesignActionSchema.extend({ last: DesignActionSchema.optional() });
export type DesignActivity = z.infer<typeof DesignActivitySchema>;

export const TurnProgressSchema = z.object({
  stage: z.enum(["chat", "browse", "design", "apply", "done"]),
  /** The turn is building a brand-new site's first draft. */
  firstBuild: z.boolean(),
  /** The first draft is being built from the owner's published card site. */
  migrating: z.boolean().optional(),
  /** While browsing: the pages being read, as host and path. */
  reading: z.array(z.string()).optional(),
  /** While looking at the draft: the page (a route) and the screen sizes being captured. */
  looking: z.object({ page: z.string(), sizes: z.array(z.string()) }).optional(),
  /** While designing: what the designer is doing right now; the client words it. */
  activity: DesignActivitySchema.optional(),
  /** A first build's draft as it first looked, shown while the designer improves it: a page and screen per image. */
  firstLook: z.array(z.object({ id: z.string(), src: z.string(), route: z.string(), viewport: z.enum(["desktop", "mobile"]) })).optional(),
});
export type TurnProgress = z.infer<typeof TurnProgressSchema>;

/** What a waiting change is, shown in the card; the worker's `ConfirmationPreview` is the same shape. */
export const ConfirmationPreviewSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("order"), number: z.number(), createdAt: z.string().datetime().optional(), items: z.array(z.string()), amount: z.string(), consequences: z.array(z.string()) }),
  z.object({ kind: z.literal("shop"), action: z.string(), name: z.string(), detail: z.string() }),
  z.object({ kind: z.literal("delete"), label: z.string(), subtitle: z.string().optional(), pages: z.array(z.string()) }),
  z.object({ kind: z.literal("restore"), part: z.enum(["design", "all"]), before: z.string() }),
  z.object({ kind: z.literal("icon"), label: z.string(), src: z.string().optional(), title: z.string() }),
  z.object({ kind: z.literal("rename"), from: z.string(), to: z.string(), host: z.string().optional() }),
  z.object({ kind: z.literal("photos"), images: z.array(z.object({ src: z.string(), alt: z.string() })) }),
  z.object({ kind: z.literal("post"), action: z.enum(["publish", "schedule", "update", "unpublish", "delete"]), title: z.string(), address: z.string(), when: z.string().optional(), shows: z.array(z.string()) }),
]);
export type ConfirmationPreview = z.infer<typeof ConfirmationPreviewSchema>;

/** A change Stax won't make until the owner confirms it. `title` is Stax's question. */
export const PendingConfirmationSchema = z.object({
  turnId: z.string(),
  id: z.string(),
  title: z.string(),
  /** A small line under the buttons. */
  note: z.string().optional(),
  preview: ConfirmationPreviewSchema.optional(),
  alternatives: z.array(z.object({ id: z.string(), title: z.string(), label: z.string(), detail: z.string() })).optional(),
  confirmLabel: z.string(),
  cancelLabel: z.string(),
});
export type PendingConfirmation = z.infer<typeof PendingConfirmationSchema>;

/**
 * A one-tap suggestion under the chat. "send" sends the label as the owner's message; "add-photos"
 * opens the photo picker; "type" puts `text` (the start of an answer only the owner can give, such as
 * "My website is ") in the message box for them to finish. A plain string (an older server) is a
 * "send" suggestion. "open" opens one of the workspace's panels.
 */
export const ChatSuggestionSchema = z.union([
  z.object({ label: z.string(), action: z.enum(["send", "add-photos"]) }),
  z.object({ label: z.string(), action: z.literal("type"), text: z.string().optional() }),
  z.object({ label: z.string(), action: z.literal("open"), panel: z.enum(["live", "publish", "domain", "site-details", "brand", "history", "content", "blog"]), name: z.string().optional(), base: z.string().optional() }),
  z.string(),
]);
export type ChatSuggestion = z.infer<typeof ChatSuggestionSchema>;
export type ChatSuggestionAction = Exclude<ChatSuggestion, string>;
/** A part of the workspace a suggestion can open. */
export type OpenPanel = Extract<ChatSuggestionAction, { action: "open" }>["panel"];

/** Reads either form as `{label, action}`. */
export function chatSuggestion(suggestion: ChatSuggestion): ChatSuggestionAction {
  return typeof suggestion === "string" ? { label: suggestion, action: "send" } : suggestion;
}

/** Persisted conversation read model from `GET|POST /_builder/sites/:siteId/conversation`. */
export const ConversationSchema = z.object({
  status: z.enum(["ready", "replying", "awaiting-input", "unavailable"]),
  messages: z.array(ConversationMessageSchema),
  suggestions: z.array(ChatSuggestionSchema),
  /** The site's canonical draft when the server answered. */
  draftId: z.string(),
  pendingTurnId: z.string().nullable(),
  /** Where the reply in progress is, from the server's checkpointed turn. */
  progress: TurnProgressSchema.nullable().optional(),
  /** A change waiting for the owner's go-ahead; the chat waits until it is answered. */
  confirmation: PendingConfirmationSchema.nullable().optional(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export interface ConfirmationDecision { turnId: string; confirmationId: string; decision: "confirmed" | "cancelled"; alternativeId?: string }

/** The longest selection an Ask Stax rewrite takes; the builder says so before sending a longer one. */
export const MAX_REWRITE_SELECTION = 1500;

/**
 * What an Ask Stax button in the writing view asks, sent with a short chat line. It names the post and
 * its saved revision, never its words: the server reads those itself for that turn.
 */
export type PostAsk =
  | { id: string; revision: string; intent: "draft" }
  | { id: string; revision: string; intent: "tags" }
  | { id: string; revision: string; intent: "seo" }
  | { id: string; revision: string; intent: "rewrite"; selection: { from: number; to: number; text: string } }
  | { id: string; revision: string; intent: "alt"; photo: { use: "cover" | "body"; assetId: string; version: number } };

/** What the owner did with a suggestion for selected words. */
export interface ProductSuggestionDecision { turnId: string; decision: "apply" | "keep" }
export interface EditorContext { area: "shop" | "blog"; section: "products" | "categories" | "orders" | "settings" | "posts" | "tags"; selectedId?: string }
/** The post open in the writing view, by revision, and the suggestion Try again was pressed on, if any. */
export interface OpenPost { id: string; revision: string; retry?: { find: string; previous: string } }
export interface PostRewriteDecision { turnId: string; index?: number; decision: "replaced" | "kept" | "retry" }

export interface SendMessageRequest {
  editorContext?: EditorContext;
  messageId: string;
  /** A short line for the chat. With `post`, never the post's words. */
  text: string;
  /** Set by the writing view's Ask Stax buttons. */
  post?: PostAsk;
  openPost?: OpenPost;
  /** The page on screen, so the admin can resolve "this heading". */
  pagePath?: string;
  /** Up to three inspiration images, each an upload of this site. */
  attachments?: Array<{ asset: AssetRef }>;
}

export const COMMENT_NOTE_LIMIT = 500;
/** Comments not yet resolved (being written or on their way to the admin) a site may have at once. */
export const OPEN_COMMENT_LIMIT = 12;

/**
 * A comment pinned on the preview. Anchored to a design node, with the tap as fractions of the
 * node's box, so it stays put when the page reflows or is seen on another screen size. `nodeType`
 * and `excerpt` are what the server found there.
 * - `draft`: written, not sent. `sent`: with the admin. `done`/`failed`/`asked`: how it went.
 */
export const PreviewCommentSchema = z.object({
  id: z.string(),
  number: z.number().int().positive(),
  pagePath: z.string(),
  nodeId: z.string(),
  itemId: z.string().optional(),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  viewport: z.enum(["phone", "desktop"]),
  note: z.string(),
  state: z.enum(["draft", "sent", "done", "failed", "asked"]),
  nodeType: z.string(),
  excerpt: z.string(),
  resultNote: z.string().optional(),
  messageId: z.string().optional(),
  request: z.discriminatedUnion("kind", [z.object({ kind: z.literal("layout"), preset: z.enum(["grid", "list", "carousel", "split", "stacked"]) }).strict(), z.object({ kind: z.literal("section"), action: z.enum(["hide", "up", "down", "remove"]) }).strict(), z.object({ kind: z.literal("add-section"), pick: z.string().min(1).max(80) }).strict()]).optional(),
});
export type PreviewComment = z.infer<typeof PreviewCommentSchema>;
export const CommentListSchema = z.object({ comments: z.array(PreviewCommentSchema) });

export interface NewComment {
  id: string;
  pagePath: string;
  nodeId: string;
  itemId?: string;
  x: number;
  y: number;
  viewport: "phone" | "desktop";
  note: string;
  request?: z.infer<typeof PreviewCommentSchema>["request"];
}

const PlanPriceSchema = z.object({ amount: z.number(), currency: z.string(), interval: z.string() });

/** `GET /_builder/sites/:siteId/publishing`: the web address and whether the plan covers publishing. */
export const DnsRecordSchema = z.object({ type: z.enum(["CNAME", "TXT"]), name: z.string(), value: z.string(), purpose: z.enum(["route", "ownership", "certificate"]) });
export type DnsRecord = z.infer<typeof DnsRecordSchema>;
export const SiteDomainSchema = z.object({
  hostname: z.string(),
  status: z.enum(["pending", "active", "failed"]),
  records: z.array(DnsRecordSchema),
  issue: z.string().nullable(),
  checkedAt: z.string().nullable(),
  /** The site's address is this domain (from the next publish). */
  inUse: z.boolean(),
});
export type SiteDomain = z.infer<typeof SiteDomainSchema>;
export type DomainProblem = "invalid" | "platform" | "taken" | "plan" | "exists" | "in-use" | "not-ready" | "none" | "failed";

export const PublishingStatusSchema = z.object({
  subdomain: z.string(),
  origin: z.string(),
  baseDomain: z.string(),
  addressChangeable: z.boolean(),
  published: z.boolean(),
  entitled: z.boolean(),
  prices: z.object({ monthly: PlanPriceSchema.nullable(), annual: PlanPriceSchema.nullable() }).nullable(),
  domain: SiteDomainSchema.nullable().optional(),
  /** Custom domains can be connected here (the hosting provider is configured). */
  domains: z.boolean().optional(),
});
export type PublishingStatus = z.infer<typeof PublishingStatusSchema>;
export type PlanPrice = z.infer<typeof PlanPriceSchema>;
export type AddressProblem = "invalid" | "reserved" | "taken" | "live";

/** Every content kind the server lists (all but the brand record); `contract.kinds.test.ts` keeps it in step. */
export const RecordKindSchema = z.enum(["profile", "copy", "service", "project", "faq", "link", "media", "contact", "social", "item", "post"]);
export type RecordKind = z.infer<typeof RecordKindSchema>;

/** `GET /_builder/sites/:siteId/content`: the owner's content library for the current draft. */
export const ContentLibrarySchema = z.object({
  entries: z.array(z.object({
    id: z.string(),
    kind: RecordKindSchema,
    label: z.string(),
    fields: z.record(z.string()),
    /** For items: the photo (a media record) shown with it. */
    image: z.object({ id: z.string(), src: z.string(), alt: z.string() }).optional(),
    usedOn: z.array(z.object({ path: z.string(), title: z.string() })),
    shared: z.boolean(),
    deletable: z.boolean(),
    /** For photos: a private preview of the image. */
    src: z.string().optional(),
    mediaType: z.string().optional(),
    originalSrc: z.string().optional(),
    crop: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }).optional(),
    /** Its position in the list it belongs to, for Move up/down. */
    list: z.object({ collectionId: z.string(), index: z.number().int().nonnegative(), count: z.number().int().nonnegative() }).optional(),
  })),
  deleted: z.array(z.object({ id: z.string(), kind: RecordKindSchema, label: z.string(), fromDraftId: z.string(), src: z.string().optional() })),
});
export type ContentLibrary = z.infer<typeof ContentLibrarySchema>;
export type LibraryEntry = ContentLibrary["entries"][number];

/** One of the owner's own photos, chosen by id; the server looks up the file. */
export type PhotoPick = { photoId: string };

/** Changes the owner makes directly: undo, and editing, deleting or restoring content. */
export type OwnerCommand =
  | { kind: "design.restore"; fromDraftId: string }
  | { kind: "draft.restore"; fromDraftId: string }
  /** `null` clears an item's optional `image` or `url`. */
  | { kind: "content.patch"; recordId: string; set: Record<string, string | null> }
  | { kind: "content.edit"; edits: Array<{ recordId: string; field: string; text: string }> }
  | { kind: "content.patch-instance"; bindingId: string; recordId: string; field: string; text: string; nodeId?: string }
  | { kind: "content.delete"; recordId: string }
  | { kind: "content.restore"; recordId: string; fromDraftId: string }
  | { kind: "brand.set"; accent?: string; typeface?: Typeface | null; fonts?: BrandFontChoice | null; palette?: string[]; voice?: BrandVoice | null }
  | { kind: "brand.restore"; fromDraftId: string }
  | { kind: "site.set"; title?: string | null; description?: string | null; icon?: AssetRef | PhotoPick | null; image?: AssetRef | PhotoPick | null; analytics?: { googleTagId: string } | null; language?: string | null; timeZone?: string | null; pages?: Record<string, { title?: string | null; description?: string | null } | null> }
  | { kind: "media.add"; records: Array<{ id: string; alt: string; asset: AssetRef; preview?: AssetRef; look?: ImageLook; replace?: true }> }
  | { kind: "media.replace"; recordId: string; sourceRecordId: string }
  | { kind: "media.crop"; recordId: string; asset: AssetRef; crop: { x: number; y: number; width: number; height: number } }
  | { kind: "media.bind"; pagePath: string; src: string; occurrence: number; recordId: string; nodeId?: string }
  | { kind: "section.hide"; pagePath: string; nodeId: string; hidden: boolean }
  | { kind: "change.undo"; actionId: string }
  | { kind: "collection.reorder"; collectionId: string; memberIds: string[] };

/** A post's own cover photo: an asset reference and its description. It is never one of the site's photo records. */
export interface PostCover { assetId: string; version: number; alt: string }
/** A post's fields as the owner works on them (`working`) or as readers see them (`live`). The words are an immutable revision. */
export interface PostCopy { title: string; slug: string; summary?: string; cover?: PostCover; tags: string[]; revision: string; publishAt?: string }
export type PostStatus = "draft" | "scheduled" | "published";
/** A post as the blog's own routes return it. It saves and goes live on its own, not with the Website's Publish. */
export interface BlogPostEntry {
  id: string;
  working: PostCopy;
  live: PostCopy | null;
  /** No live copy is a draft; a live date still ahead is scheduled; otherwise published. */
  status: PostStatus;
  /** The working copy differs from what readers see: Update post shares it. */
  hasChanges: boolean;
  firstPublishedAt: string | null;
  /** When the post was last saved (ISO time): the posts list says when it was edited. */
  updatedAt?: string;
  /** Sent back with every change, so a post changed somewhere else is never overwritten. */
  rowVersion: number;
  /** Whether Undo has a change to take back. */
  undoable: boolean;
  redoable: boolean;
  coverSrc?: string;
}
/** A change to a post's working copy; `null` clears an optional field. */
export type PostChange = Partial<Omit<PostCopy, "summary" | "cover" | "publishAt">> & { summary?: string | null; cover?: PostCover | null; publishAt?: string | null };
/** Explicit, confirmed steps on a post. Each goes live at once, except Undo, which only changes the working copy. */
export type PostAction = "publish" | "schedule" | "update" | "unpublish" | "undo" | "redo" | "delete";
export interface BlogSettings { name: string; base: string; enabled: boolean; addToMenu: boolean; latestOnHome: boolean }
/** A photo that belongs to a post, with where the builder can show it. `alt` is set when it was copied from a site photo. */
export interface PostPhoto { assetId: string; version: number; src: string; alt?: string }
export interface PostSeo { title?: string; description?: string; imageCrop?: { x: number; y: number; width: number; height: number } }
export interface PostRevision { bodyMd: string; seo: PostSeo; wordCount: number; /** The photos the body uses that exist, each with its preview address. */ photos?: PostPhoto[] }
/** `posts[].coverSrc` is where the post's cover shows; `photos` are the site's own photos, which a post can copy but never depends on. */
export interface BlogIndex { total?: number; counts?: { all: number; draft: number; scheduled: number; published: number }; tagCounts?: Record<string, number>; settings: BlogSettings | null; version: number; posts: BlogPostEntry[]; tags: Array<{ id: string; label: string; slug: string }>; photos: Array<{ id: string; src: string; alt: string }> }

/** Shop catalogue contracts. No checkout, orders, public file URLs or file bytes in this slice. */
export const ShopProductEntrySchema = z.object({
  id: z.string(), details: ProductDetailsSchema, availability: z.enum(["hidden", "available"]),
  variants: z.array(ShopVariantSchema.extend({ rowVersion: z.number().int().positive() })),
  rowVersion: z.number().int().positive(), createdAt: z.string(), updatedAt: z.string(),
  status: z.enum(["hidden", "available", "sold-out"]).optional(),
  photoSources: z.array(z.object({ assetId: z.string(), version: z.number().int(), alt: z.string(), src: z.string() })).optional(),
});
export const ShopStockSchema = z.object({ variantId: z.string(), tracked: z.boolean(), quantity: z.number().int().nonnegative(), rowVersion: z.number().int().positive() });
export const ShopFileSchema = ShopFileInputSchema.extend({ productId: z.string(), version: z.number().int().positive(), createdAt: z.string() });
export const ShopIndexSchema = z.object({
  total: z.number().int().nonnegative().optional(), categoryCounts: z.record(z.string(), z.number()).optional(), customizationCounts: z.record(z.string(), z.number()).optional(),
  settings: ShopSettingsSchema.nullable(), version: z.number().int().nonnegative(), rowVersion: z.number().int().nonnegative(),
  products: z.array(ShopProductEntrySchema),
  categories: z.array(ShopCategoryInputSchema.extend({ id: z.string(), rowVersion: z.number().int().positive() })),
  customizationSets: z.array(z.object({ id: z.string(), rowVersion: z.number().int().positive(), name: z.string(), currency: z.string(), min: z.number().int(), max: z.number().int(), choices: z.array(z.object({ id: z.string(), name: z.string(), price: z.number().int() })) }).refine((set) => CustomizationSetInputSchema.safeParse({ name: set.name, currency: set.currency, min: set.min, max: set.max, choices: set.choices }).success)),
  stock: z.array(ShopStockSchema), files: z.array(ShopFileSchema),
});
const ProductHistoryCopySchema = z.object({ details: ProductDetailsSchema, variants: z.array(ShopVariantSchema), availability: z.enum(["hidden", "available"]) });
export const ShopHistorySchema = z.array(z.object({ seq: z.number().int(), action: z.string(), actor: z.string(), before: ProductHistoryCopySchema.nullable(), after: ProductHistoryCopySchema.nullable(), undoes: z.array(z.number().int()).nullable(), at: z.string() }));
export { ProductRevisionSchema as ShopProductRevisionSchema };
export type ShopProductEntry = z.infer<typeof ShopProductEntrySchema>;
export type ShopIndex = z.infer<typeof ShopIndexSchema>;
export type ShopHistory = z.infer<typeof ShopHistorySchema>;
export interface ShopApi {
  lists?: OwnerListsApi;
  commerce?: import("./commerceApi").CommerceApi;
  orders?: import("./ordersApi").OrdersApi;
  getShop(siteId: string, signal?: AbortSignal): Promise<ShopIndex>;
  saveShopSettings(siteId: string, settings: ShopSettings, rowVersion: number): Promise<ShopIndex>;
  createProduct(siteId: string, input: { id: string; name: string; slug: string; price?: number | null }): Promise<ShopProductEntry>;
  saveProduct(siteId: string, id: string, rowVersion: number, set: ProductChange): Promise<ShopProductEntry>;
  saveProductOptions(siteId: string, id: string, rowVersion: number, options: ShopOption[]): Promise<ShopProductEntry>;
  saveProductVariants(siteId: string, id: string, rowVersion: number, variants: ShopVariant[]): Promise<ShopProductEntry>;
  setProductAvailability(siteId: string, id: string, rowVersion: number, availability: "hidden" | "available"): Promise<ShopProductEntry>;
  deleteProduct(siteId: string, id: string, rowVersion: number): Promise<void>;
  undoProduct(siteId: string, id: string, rowVersion: number): Promise<ShopProductEntry>;
  restoreProduct(siteId: string, id: string, rowVersion: number, seq: number): Promise<ShopProductEntry>;
  getProductHistory(siteId: string, id: string, signal?: AbortSignal): Promise<ShopHistory>;
  saveProductRevision(siteId: string, input: ShopProductRevision): Promise<{ digest: string }>;
  getProductRevision(siteId: string, digest: string, signal?: AbortSignal): Promise<ShopProductRevision>;
  saveVariantStock(siteId: string, variantId: string, rowVersion: number, stock: ShopStockInput): Promise<z.infer<typeof ShopStockSchema>>;
  saveShopCategory(siteId: string, id: string, rowVersion: number, set: ShopCategoryInput): Promise<ShopIndex>;
  deleteShopCategory(siteId: string, id: string, rowVersion: number): Promise<ShopIndex>;
  saveCustomizationSet(siteId: string, id: string, rowVersion: number, set: CustomizationSetInput): Promise<ShopIndex>;
  deleteCustomizationSet(siteId: string, id: string, rowVersion: number): Promise<ShopIndex>;
  registerProductPhoto(siteId: string, id: string, input: { photoId: string } | { asset: AssetRef }): Promise<PostPhoto>;
  uploadProductFile(siteId: string, id: string, fileId: string, name: string, body: Blob, options?: { signal?: AbortSignal; onProgress?: (percent: number) => void }): Promise<z.infer<typeof ShopFileSchema>>;
  registerProductFile(siteId: string, id: string, input: ShopFileInput): Promise<z.infer<typeof ShopFileSchema>>;
}

/** A stored upload, as `POST /_builder/sites/:siteId/uploads` returns it. */
export const AssetRefSchema = z.object({
  assetId: z.string(),
  version: z.number().int(),
  objectKey: z.string(),
  mimeType: z.enum(["image/png", "image/jpeg", "image/webp", "image/svg+xml"]),
  sha256: z.string(),
});
export type AssetRef = z.infer<typeof AssetRefSchema>;

/** What the admin saw in a photo, as `POST /_builder/sites/:siteId/photos/look` returns it. */
export const ImageLookSchema = z.object({
  description: z.string().min(1).max(160),
  kind: z.enum(["logo", "portrait", "product", "place", "work", "other"]),
  colours: z.array(z.string().regex(/^#[a-f0-9]{6}$/)).max(5),
  focus: z.enum(["center", "top", "bottom", "left", "right"]),
  notes: z.string().max(300),
}).strict();
export type ImageLook = z.infer<typeof ImageLookSchema>;

export interface OwnerActionRequest {
  actionId: string;
  baseDraftId: string;
  command: OwnerCommand;
}

/** Everything the workspace needs from the server. Injectable so a local fixture can stand in. */
export interface BuilderApi {
  lists?: OwnerListsApi;
  /** Catalogue backend client. Local demonstrations can omit it until the Shop screens are built. */
  shop?: ShopApi;
  getWorkspace(siteId: string, signal?: AbortSignal): Promise<Workspace>;
  getAction(siteId: string, actionId: string, signal?: AbortSignal): Promise<ActionSummary>;
  preparePublication(siteId: string, request: PublicationRequest): Promise<ActionSummary>;
  confirmPublication(siteId: string, actionId: string, proposalDigest: string): Promise<ActionSummary>;
  /** Resolves with the server's action even when it could not be cancelled because it is already running. */
  cancelAction(siteId: string, actionId: string): Promise<{ action: ActionSummary; cancelled: boolean }>;
  getConversation(siteId: string, signal?: AbortSignal): Promise<Conversation>;
  /** Idempotent by message ID: resending the same ID and text returns the existing state. */
  sendMessage(siteId: string, request: SendMessageRequest): Promise<Conversation>;
  /** Answers a waiting confirmation. Idempotent: the same answer again returns the current state. */
  decideConfirmation(siteId: string, decision: ConfirmationDecision): Promise<Conversation>;
  /** Records Replace, Keep mine or Try again on a rewrite suggestion, so it reaches the chat and Stax's history. Idempotent. */
  decideProductSuggestion(siteId: string, decision: ProductSuggestionDecision): Promise<Conversation>;
  decidePostRewrite(siteId: string, decision: PostRewriteDecision): Promise<Conversation>;
  /** Confirms a prepared draft change (e.g. a design) with its exact proposal digest. */
  confirmAction(siteId: string, actionId: string, proposalDigest: string): Promise<ActionSummary>;
  /** Submits an owner's own draft change (undo, or a content edit). Idempotent by action ID. */
  submitAction(siteId: string, request: OwnerActionRequest): Promise<ActionSummary>;
  getContent(siteId: string, signal?: AbortSignal): Promise<ContentLibrary>;
  getBlog(siteId: string, signal?: AbortSignal): Promise<BlogIndex>;
  /**
   * The blog's own writes. Settings, tags and posts save straight to the blog and go live on their own; none of it is a Website draft change.
   * A refusal rejects with `BlogRefusedError` (its message is for the owner); a post changed somewhere else rejects with a 409 `BuilderRequestError`.
   */
  saveBlogSettings(siteId: string, input: { name: string; base: string; enabled: boolean; addToMenu?: boolean; latestOnHome?: boolean }): Promise<BlogIndex>;
  createBlogTag(siteId: string, input: { id: string; label: string; slug: string }): Promise<BlogIndex>;
  updateBlogTag(siteId: string, id: string, input: { label?: string; slug?: string }): Promise<BlogIndex>;
  deleteBlogTag(siteId: string, id: string): Promise<BlogIndex>;
  createPost(siteId: string, input: { id: string; working: PostCopy }): Promise<BlogPostEntry>;
  /** Saves the post's working copy (autosave). Readers of a published post are unaffected until Update post. */
  savePost(siteId: string, id: string, rowVersion: number, set: PostChange): Promise<BlogPostEntry>;
  /** Publish, Schedule, Update post, Unpublish and Undo answer with the post; Remove answers with null. */
  postAction(siteId: string, id: string, action: PostAction, rowVersion: number): Promise<BlogPostEntry | null>;
  savePostRevision(siteId: string, input: { postId?: string; bodyMd: string; seo?: PostSeo }): Promise<{ digest: string; wordCount: number }>;
  getPostRevision(siteId: string, digest: string, signal?: AbortSignal): Promise<PostRevision>;
  getPublishing(siteId: string, signal?: AbortSignal): Promise<PublishingStatus>;
  /** Chooses the web address before the site first goes live; resolves with the problem when it can't. */
  changeAddress(siteId: string, subdomain: string): Promise<{ origin: string } | { problem: AddressProblem }>;
  /** A checkout for the plan that covers publishing; resolves with where to send the owner. */
  startCheckout(siteId: string, cycle: "monthly" | "annual"): Promise<string>;
  addDomain(siteId: string, hostname: string): Promise<SiteDomain | { problem: DomainProblem }>;
  checkDomain(siteId: string): Promise<SiteDomain | { problem: DomainProblem }>;
  /** Makes the verified domain (or the platform address) the site's address from the next publish. */
  switchAddress(siteId: string, to: "domain" | "platform"): Promise<{ origin: string } | { problem: DomainProblem }>;
  removeDomain(siteId: string): Promise<{ removed: true } | { problem: DomainProblem }>;
  /** Stores one prepared photo privately; it appears nowhere until added with `media.add`. */
  uploadPhoto(siteId: string, photo: Blob): Promise<AssetRef>;
  /**
   * Makes a photo the post's own: an upload just made (`asset`), or a copy of a site photo (`photoId`, which also
   * returns its current description). Neither adds a photo to the site's library.
   */
  addPostPhoto(siteId: string, source: { asset: AssetRef } | { photoId: string }): Promise<PostPhoto>;
  /**
   * Has the admin look at an uploaded photo (its AI copy) and say what it shows, or, for an
   * inspiration image (`"reference"`), describe its style. Optional help: it can fail.
   */
  describePhoto(siteId: string, asset: AssetRef, purpose?: "photo" | "reference"): Promise<ImageLook>;
  listComments(siteId: string, signal?: AbortSignal): Promise<PreviewComment[]>;
  /** Pins a comment to a node of the current draft. Idempotent by comment ID. */
  createComment(siteId: string, comment: NewComment): Promise<PreviewComment[]>;
  /** Changes the note of a comment that hasn't been sent. */
  updateComment(siteId: string, commentId: string, note: string): Promise<PreviewComment[]>;
  /** Removes a comment that hasn't been sent, or dismisses a resolved one. */
  deleteComment(siteId: string, commentId: string): Promise<PreviewComment[]>;
  /**
   * Sends comments to the admin as one message, idempotent by message ID. Resolves with `moved` (the
   * comments whose part of the page is gone) instead when it can't, and sends nothing.
   */
  sendComments(siteId: string, request: { messageId: string; ids: string[] }): Promise<Conversation | { moved: string[] }>;
}
